import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { IconOpenNew } from '@amtrav/icons';
import { Badge, Heading3 } from '@amtrav/view-components';
import { MatchMedia, media } from '@amtrav/themes';
import { Flex, Box } from '@rebass/grid';
import { getTokenValue, SPACE, THEME_TOKENS } from '@amtrav/theme-system';
import isMobileApp from '../../helpers/isMobileApp';

const USER_ROLE = {
  administrator: 'A',
  coordinator: 'C',
};

const UserSummary = styled(Box)`
  background-color: ${getTokenValue(THEME_TOKENS.base.color.neutralBackground)};
  margin: -8px -8px 8px -8px;
  padding: ${SPACE.size12} ${SPACE.size24};
  border-bottom: 1px solid ${getTokenValue(THEME_TOKENS.card.color.border)};
  border-top-right-radius: ${getTokenValue(THEME_TOKENS.card.size.borderRadiusLarge)};
  border-top-left-radius: ${getTokenValue(THEME_TOKENS.card.size.borderRadiusLarge)};
`;

const EmailString = styled(Box)`
  color: ${getTokenValue(THEME_TOKENS.base.color.secondaryText)};
  font-weight: normal;
  font-size: 14px;
`;

const CompanyName = styled(Box)`
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
`;

const showLogout = !isMobileApp();

const LinkOption = styled(Flex)`
  cursor: pointer;
  font-weight: bold;
  color: ${getTokenValue(THEME_TOKENS.base.color.action)};
  min-width: 200px;
  align-items: center;
  font-size: 16px;

  ${media.Large`
    
    &:hover {
      background-color: ${getTokenValue(THEME_TOKENS.textInput.color.backgroundHover)};
      border-radius: ${getTokenValue(THEME_TOKENS.card.size.borderRadiusMedium)};
    }
  `};
`;

const ImportantOption = styled(LinkOption)`
  color: ${getTokenValue(THEME_TOKENS.base.color.danger)};

  ${media.Large`
    &:hover {
      background: ${getTokenValue(THEME_TOKENS.base.color.dangerBackground)};
    }
  `};
`;

const MenuPopover = ({
  userRole = '',
  navPolicyDoc = '',
  showProfileLink = false,
  email = '',
  userName = '',
  companyName = '',
}) => {
  return (
    <Box p={SPACE.size8}>
      <MatchMedia.Medium>
        {userName ? (
          <UserSummary>
            {userName ? <Heading3>{userName}</Heading3> : null}
            {companyName ? <CompanyName>{companyName}</CompanyName> : null}
          </UserSummary>
        ) : null}
      </MatchMedia.Medium>
      {showProfileLink ? (
        <LinkOption
          onClick={() => {
            window.document.location = '/profile/';
          }}
          p={SPACE.size12}
          pl={[SPACE.size16, SPACE.size16, SPACE.size16, 3]}>
          <Flex flexDirection="column">
            <Flex justifyContent="space-between" alignItems="center">
              <Box pr={[3, 3, 3, 4]}>
                My profile
                <EmailString>{email?.toLocaleLowerCase()}</EmailString>
              </Box>
              {userRole === USER_ROLE.administrator ? <Badge kind="success">Administrator</Badge> : null}
              {userRole === USER_ROLE.coordinator ? <Badge kind="warning">Coordinator</Badge> : null}
            </Flex>
          </Flex>
        </LinkOption>
      ) : null}
      {navPolicyDoc ? (
        <LinkOption
          hideBorder={!showLogout}
          onClick={() => {
            window.open(`/policies-document/${navPolicyDoc}`);
          }}
          p={SPACE.size12}
          pl={[SPACE.size16, SPACE.size16, SPACE.size16, 3]}>
          <Flex>
            <Box pr={2}>View policy</Box>
            <Flex>
              <IconOpenNew />
            </Flex>
          </Flex>
        </LinkOption>
      ) : null}
      {showLogout ? (
        <ImportantOption
          onClick={() => {
            window.document.location = '/logout/';
          }}
          p={SPACE.size12}
          pl={[SPACE.size16, SPACE.size16, SPACE.size16, 3]}>
          Sign out
        </ImportantOption>
      ) : null}
    </Box>
  );
};

MenuPopover.propTypes = {
  userRole: PropTypes.string,
  /**
   * Name of travel policy document
   */
  navPolicyDoc: PropTypes.string,
  showProfileLink: PropTypes.bool,
  email: PropTypes.string,
  userName: PropTypes.string,
  companyName: PropTypes.string,
};

export default MenuPopover;
